// Role-based Access Control Configuration
// Defines what each role can access in the CMS

export const ROLES = {
  MASTER_ADMIN: 'master_admin',
  ADMIN: 'admin',
  EDITOR: 'editor'
};

export const PERMISSIONS = {
  // Navigation & Pages
  DASHBOARD: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  USERS: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  MAPS_CHALLENGES: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  STATISTICS: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  NFTS: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  IMAGE_GALLERY: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],

  // Master Admin Only
  ADMIN_MANAGEMENT: [ROLES.MASTER_ADMIN],
  BLOCKCHAIN_SETTINGS: [ROLES.MASTER_ADMIN],
  TERMINAL: [ROLES.MASTER_ADMIN],
  SYSTEM_SETTINGS: [ROLES.MASTER_ADMIN],

  // Master Admin & Admin
  SETTINGS_GENERAL: [ROLES.MASTER_ADMIN, ROLES.ADMIN],
  SETTINGS_USERS: [ROLES.MASTER_ADMIN, ROLES.ADMIN],
  SETTINGS_EMAIL: [ROLES.MASTER_ADMIN, ROLES.ADMIN],
  SETTINGS_SECURITY: [ROLES.MASTER_ADMIN, ROLES.ADMIN],

  // Public Website Management (All roles)
  LANDING_PAGE: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],

  // NFT Management
  NFT_TEMPLATES: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  NFT_MINTING: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],

  // User Actions
  USER_CREATE: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  USER_EDIT: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  USER_DELETE: [ROLES.MASTER_ADMIN, ROLES.ADMIN],
  USER_BAN: [ROLES.MASTER_ADMIN, ROLES.ADMIN],

  // Challenge Actions
  CHALLENGE_CREATE: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  CHALLENGE_EDIT: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  CHALLENGE_DELETE: [ROLES.MASTER_ADMIN, ROLES.ADMIN],

  // Content Management
  CONTENT_EDIT: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
  CONTENT_PUBLISH: [ROLES.MASTER_ADMIN, ROLES.ADMIN, ROLES.EDITOR],
};

// Navigation items with role-based visibility
export const getNavItems = (userRole) => {
  const items = [
    {
      name: 'Dashboard',
      path: '/',
      icon: '📊',
      roles: PERMISSIONS.DASHBOARD
    },
    {
      name: 'Users',
      path: '/users',
      icon: '👥',
      roles: PERMISSIONS.USERS
    },
    {
      name: 'Maps & Challenges',
      path: '/map',
      icon: '🗺️',
      roles: PERMISSIONS.MAPS_CHALLENGES
    },
    {
      name: 'Statistics',
      path: '/statistics',
      icon: '📈',
      roles: PERMISSIONS.STATISTICS
    },
    {
      name: 'NFTs',
      path: '/nfts',
      icon: '🎨',
      roles: PERMISSIONS.NFTS
    },
    {
      name: 'Image Gallery',
      path: '/gallery',
      icon: '🖼️',
      roles: PERMISSIONS.IMAGE_GALLERY
    },
    {
      name: 'Public Website',
      path: '/public-website',
      icon: '🌐',
      roles: PERMISSIONS.LANDING_PAGE,
      children: [
        { name: 'Landing Page', path: '/landing-page', roles: PERMISSIONS.LANDING_PAGE },
        { name: 'Gallery', path: '/gallery', roles: PERMISSIONS.IMAGE_GALLERY }
      ]
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: '⚙️',
      roles: [ROLES.MASTER_ADMIN, ROLES.ADMIN] // Editors can't access settings
    },
    {
      name: 'Admin Management',
      path: '/admin-management',
      icon: '👨‍💼',
      roles: PERMISSIONS.ADMIN_MANAGEMENT,
      badge: 'Master'
    },
    {
      name: 'Terminal',
      path: '/terminal',
      icon: '💻',
      roles: PERMISSIONS.TERMINAL,
      badge: 'Master'
    }
  ];

  // Filter items based on user role
  return items.filter(item => item.roles.includes(userRole));
};

// Check if user has permission
export const hasPermission = (userRole, permission) => {
  return PERMISSIONS[permission]?.includes(userRole) || false;
};

// Get user role display name
export const getRoleDisplayName = (role) => {
  const names = {
    [ROLES.MASTER_ADMIN]: 'Master Admin',
    [ROLES.ADMIN]: 'Site Manager',
    [ROLES.EDITOR]: 'Editor'
  };
  return names[role] || role;
};

// Get user role description
export const getRoleDescription = (role) => {
  const descriptions = {
    [ROLES.MASTER_ADMIN]: 'Platform owner with full system access',
    [ROLES.ADMIN]: 'Site manager with settings and administrative access',
    [ROLES.EDITOR]: 'Content creation and statistics access'
  };
  return descriptions[role] || '';
};

// Documentation sections by role
export const getDocumentationSections = (userRole) => {
  const allSections = {
    editor: [
      { name: 'CMS User Guide', file: 'CMS_USER_GUIDE.md' },
      { name: 'Maps & Challenges', file: 'MAPS_CHALLENGES_GUIDE.md' },
      { name: 'User Management', file: 'USER_MANAGEMENT_GUIDE.md' },
      { name: 'Gallery Management', file: 'GALLERY_MANAGEMENT_GUIDE.md' },
      { name: 'NFT Minting', file: 'NFT_MINTING_GUIDE.md' },
      { name: 'Landing Page System', file: 'LANDING_PAGE_SYSTEM.md' },
      { name: 'Mobile App Features', file: 'MOBILE_APP_FEATURES.md' }
    ],
    admin: [
      { name: 'CMS User Guide', file: 'CMS_USER_GUIDE.md' },
      { name: 'Maps & Challenges', file: 'MAPS_CHALLENGES_GUIDE.md' },
      { name: 'AI Challenge Generator', file: 'AI_CHALLENGE_GENERATOR_GUIDE.md' },
      { name: 'User Management', file: 'USER_MANAGEMENT_GUIDE.md' },
      { name: 'Gallery Management', file: 'GALLERY_MANAGEMENT_GUIDE.md' },
      { name: 'NFT Minting', file: 'NFT_MINTING_GUIDE.md' },
      { name: 'Settings Configuration', file: 'SETTINGS_CONFIGURATION.md' },
      { name: 'Statistics & Analytics', file: 'STATISTICS_ANALYTICS.md' },
      { name: 'Landing Page System', file: 'LANDING_PAGE_SYSTEM.md' },
      { name: 'Mobile App Features', file: 'MOBILE_APP_FEATURES.md' }
    ],
    master_admin: [
      // All admin docs
      { name: 'CMS User Guide', file: 'CMS_USER_GUIDE.md' },
      { name: 'Maps & Challenges', file: 'MAPS_CHALLENGES_GUIDE.md' },
      { name: 'AI Challenge Generator', file: 'AI_CHALLENGE_GENERATOR_GUIDE.md' },
      { name: 'User Management', file: 'USER_MANAGEMENT_GUIDE.md' },
      { name: 'Gallery Management', file: 'GALLERY_MANAGEMENT_GUIDE.md' },
      { name: 'NFT Minting', file: 'NFT_MINTING_GUIDE.md' },
      { name: 'Terminal Access', file: 'TERMINAL_ACCESS_GUIDE.md' },
      { name: 'Settings Configuration', file: 'SETTINGS_CONFIGURATION.md' },
      { name: 'Statistics & Analytics', file: 'STATISTICS_ANALYTICS.md' },
      { name: 'Landing Page System', file: 'LANDING_PAGE_SYSTEM.md' },
      { name: 'Mobile App Features', file: 'MOBILE_APP_FEATURES.md' },
      // Master-only docs
      { name: '🔒 Deployment Guide', file: 'DEPLOYMENT.md', masterOnly: true },
      { name: '🔒 Custodial Wallet System', file: 'CUSTODIAL_WALLET_SYSTEM.md', masterOnly: true },
      { name: '🔒 Project Requirements', file: 'PROJECT_REQUIREMENTS.md', masterOnly: true },
      { name: '🔒 Product Overview', file: 'PRODUCT_OVERVIEW.md', masterOnly: true },
      { name: '🔒 Progress Summary', file: 'PROGRESS_SUMMARY.md', masterOnly: true }
    ]
  };

  return allSections[userRole] || allSections.editor;
};

export default {
  ROLES,
  PERMISSIONS,
  getNavItems,
  hasPermission,
  getRoleDisplayName,
  getRoleDescription,
  getDocumentationSections
};
